import { defineStore } from "pinia";
import { DateTime } from "luxon";
import padStart from "just-left-pad";

import type { PublisherStatContainer } from "@opentok/client";
import type { VideoCallOptions, VideoCallState } from "./Models/State";
import { StateNamespace } from "@/Modules/App/Models/State/StateNamespace";
import {
  VIDEO_CALL_STATUS,
  VIDEO_CALL_STATUS_CALLING,
  type VIDEO_CALL_EXCEPTIONS,
  type VideoCallConfig,
} from "./Components/definitions";
import type {
  VIDEO_CALL_COMPLETE_REASON,
  VideoCallError,
  VideoCallRatingQuery,
} from "../Models/Api/VideoCalls";

import EndVideoCallActionHandler from "./ActionHandlers/EndVideoCall";
import HangUpVideoCallActionHandler from "./ActionHandlers/HangUpVideoCall";
import SendVideoCallLogsActionHandler from "./ActionHandlers/SendVideoCallLogs";
import StartVideoCallActionHandler from "./ActionHandlers/StartVideoCall";
import CheckVideoCallInProgressActionHandler from "./ActionHandlers/CheckVideoCallInProgress";
import SendVideoCallAudioHandler from "./ActionHandlers/SendVideoCallAudio";
import SendVideoCallRating from "./ActionHandlers/SendVideoCallRating";
import VideoCallConfigAH from "./ActionHandlers/VideoCallConfig";

import { isValidString } from "@/Utils/String";
import { waitFor } from "@/Utils/Promises";
import { hasValue } from "@/Utils/Objects";
import { MILLISECONDS_TO_SECONDS } from "@/Utils/Date";
import { log } from "@/Utils/Debug";

const videoCallState: VideoCallState = {
  isLoading: false,
  roomId: undefined,
  remoteUser: undefined,
  isHiddenVideoCall: false,
  timeCounter: { startDate: new Date(), currentDate: new Date() },
  showVideoCallError: false,
  patientPermissionError: false,
  isTranscriptionEnabled: false,
  isRatingRequired: false,
  errors: [],
  sessionId: undefined,
  token: undefined,
  status: undefined,
  showConfig: false,
  nextCallOptions: undefined,
};

const pad = (num: number) => padStart(num.toFixed(0).toString(), 2, "0");

export const useVideoCallStore = defineStore(StateNamespace.VIDEO_CALL, {
  state: () => ({ ...videoCallState }),
  getters: {
    durationStringWithSecs(state) {
      const { startDate, currentDate } = state.timeCounter;

      if (startDate === undefined || currentDate === undefined) return "00:00:00";

      const date = DateTime.fromJSDate(currentDate);
      const { hours, minutes, seconds } = date.diff(DateTime.fromJSDate(startDate), [
        "hours",
        "minutes",
        "seconds",
      ]);

      if (seconds < 0) return "00:00:00";

      return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    },

    durationString(state) {
      const { startDate, currentDate } = state.timeCounter;

      if (startDate === undefined || currentDate === undefined) return "00:00";

      const date = DateTime.fromJSDate(currentDate);
      const { minutes, seconds } = date.diff(DateTime.fromJSDate(startDate), ["minutes", "seconds"]);

      return `${pad(minutes)}:${pad(seconds)}`;
    },

    isInCall(state): boolean {
      return VIDEO_CALL_STATUS_CALLING.includes(state.status);
    },
  },
  actions: {
    checkVideoCallInProgress(chatId?: string): Promise<boolean> {
      return CheckVideoCallInProgressActionHandler(chatId);
    },

    startTherapistCall(chatId: string, check: boolean = true) {
      // ONLY CALLED BY THERAPIST ON CAM CLICK
      return StartVideoCallActionHandler(chatId, check);
    },

    stopLoadingVideocall() {
      this.isLoading = false;
    },

    setupVideoCall(options: VideoCallOptions) {
      log("SETUP VIDEO CALL", options);
      this.nextCallOptions = undefined;
      this.remoteUser = options.remoteUser;

      this.roomId = options.roomId;
      this.sessionId = options.sessionId;
      this.token = options.token;

      this.isTranscriptionEnabled = options.isTranscribed;
      this.isRatingRequired = !!options.isRatingRequired;

      this.timeCounter = { startDate: new Date(), currentDate: new Date() };
      this.errors = [];
      this.status = VIDEO_CALL_STATUS.PERMISSIONS;
    },

    async sendAudio(audio: Blob): Promise<boolean> {
      if (isValidString(this.roomId)) {
        return SendVideoCallAudioHandler(audio, this.roomId);
      }

      return false;
    },

    updateStatus(status?: VIDEO_CALL_STATUS) {
      this.status = status;
    },

    async endVideoCall() {
      this.remoteUser = undefined;
      this.isLoading = false;
      this.isHiddenVideoCall = false;
      this.token = undefined;
      this.sessionId = undefined;
      this.isTranscriptionEnabled = false;
      this.status = undefined;

      if (!this.isRatingRequired) {
        this.cleanRaiting();
      }
    },

    async hangDownVideoCall(roomId?: string, reason?: VIDEO_CALL_COMPLETE_REASON) {
      if (isValidString(roomId)) {
        await EndVideoCallActionHandler(roomId, reason);
      }

      this.clean();
    },

    hangUpVideoCall() {
      if (isValidString(this.roomId)) {
        HangUpVideoCallActionHandler(this.roomId);
      }
    },

    clean() {
      this.isLoading = false;
      this.token = undefined;
      this.sessionId = undefined;
      this.remoteUser = undefined;
      this.isHiddenVideoCall = false;
      this.isTranscriptionEnabled = false;
      this.errors = [];

      if (!this.isRatingRequired) {
        this.cleanRaiting();
      }

      waitFor(0.5).then(() => {
        if (hasValue(navigator) && hasValue(navigator.mediaDevices)) {
          navigator.mediaDevices.dispatchEvent(new Event("devicechange"));
        }
      });
    },

    async cleanRaiting() {
      this.isRatingRequired = false;
      this.roomId = undefined;
    },

    disableRating() {
      this.isRatingRequired = false;
    },

    enableRating() {
      log("[VIDEOCALL]: ENABLE RATING");

      this.isRatingRequired = true;
    },

    showVideoCall() {
      this.isHiddenVideoCall = false;
    },

    hideVideoCall() {
      this.isHiddenVideoCall = true;
    },

    toggleHideVideoCall() {
      this.isHiddenVideoCall = !this.isHiddenVideoCall;
    },

    updateCurrentDate(date: Date) {
      this.timeCounter.currentDate = date;
    },

    updateStartDate(date: Date) {
      this.timeCounter.startDate = date;
    },

    updateVideoCallError(val: boolean) {
      this.showVideoCallError = val;
    },

    addError(errCode: VIDEO_CALL_EXCEPTIONS, exception?: unknown) {
      const newError: VideoCallError = {
        reason: errCode,
        timestamp: new Date().getTime() / MILLISECONDS_TO_SECONDS,
      };

      if (exception) newError.exception = exception;

      this.errors.push(newError);
    },

    sendVideoCallLogs(roomId: string, stats?: PublisherStatContainer): Promise<void> {
      return SendVideoCallLogsActionHandler(this)(roomId, stats);
    },

    sendVideoCallRating(query: VideoCallRatingQuery): Promise<boolean> {
      if (!isValidString(this.roomId)) return Promise.resolve(false);

      return SendVideoCallRating(this.roomId, query);
    },

    /* VIDEO CALL CONFIG */

    updateNextCallOptions(options: VideoCallOptions | undefined) {
      this.nextCallOptions = options;
    },

    updateIsTranscriptionEnabled(value: boolean) {
      this.isTranscriptionEnabled = value;
    },

    updateShowConfig(value: boolean) {
      this.showConfig = value;
    },

    getConfig(): VideoCallConfig | undefined {
      return VideoCallConfigAH.GetActionHandler();
    },

    isValidConfig(): Promise<boolean> {
      return VideoCallConfigAH.IsValidActionHandler();
    },

    saveConfig(config: VideoCallConfig) {
      VideoCallConfigAH.SaveActionHandler(config);
    },

    clearConfig() {
      VideoCallConfigAH.ClearActionHandler();
    },
    /* VIDEO CALL CONFIG */
  },
});

export type VideoCallStore = ReturnType<typeof useVideoCallStore>;

export default useVideoCallStore;
